/** Setup steps: VAT, bank, numbering and review. */
import { Button } from "../../components/ui/Button.js";
import { Card } from "@primereact/ui/card";
import { Checkbox } from "@primereact/ui/checkbox";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { InputNumber } from "@primereact/ui/inputnumber";
import { Select } from "@primereact/ui/select";
import { ToggleSwitch as Toggle } from "@primereact/ui/toggleswitch";
import { translateError } from "../../lib/form.js";
import {
  INDUSTRY_DEFAULT_LABELS,
  type SetupForm,
  useSetup,
  ReviewSection,
  ReviewRow,
} from "./wizard.js";

export function VatStep() {
  const { t, register, setValue, errors, rates, values } = useSetup();
  return (
    <div className="space-y-5">
      <div>
        <label
          className="flex items-center gap-3 text-sm font-medium"
          htmlFor="vat-registered"
        >
          <Toggle.Root
            inputId="vat-registered"
            checked={values.vatRegistered}
            onCheckedChange={(event: { checked: boolean }) =>
              ((checked) => setValue("vatRegistered", checked))(event.checked)
            }
          >
            <Toggle.Control>
              <Toggle.Handle></Toggle.Handle>
            </Toggle.Control>
          </Toggle.Root>
          <span>{t("setup.vat.registered")}</span>
        </label>
        <small className="mt-1 block text-app-muted">
          {t("setup.vat.registeredHint")}
        </small>
      </div>
      <fieldset>
        <legend className="text-sm font-medium text-app-text">
          {t("setup.vat.rates")}
        </legend>
        <p className="mt-0.5 text-sm text-app-muted">
          {t("setup.vat.ratesHint")}
        </p>
        <div className="mt-3 space-y-3">
          {rates.fields.map((field, index) => (
            <div
              key={field.id}
              className="flex flex-wrap items-end gap-3 rounded-lg bg-app-background p-3"
            >
              <Field
                label={t("setup.vat.rateName")}
                htmlFor={`rate-name-${index}`}
                error={translateError(
                  errors.taxRates?.[index]?.name?.message,
                  t,
                )}
                className="min-w-40 flex-1"
              >
                <Input
                  id={`rate-name-${index}`}
                  invalid={!!errors.taxRates?.[index]?.name}
                  {...register(`taxRates.${index}.name`)}
                />
              </Field>
              <Field
                label={t("setup.vat.rateValue")}
                htmlFor={`rate-value-${index}`}
                error={translateError(
                  errors.taxRates?.[index]?.rate?.message,
                  t,
                )}
                className="w-28"
              >
                <Input
                  id={`rate-value-${index}`}
                  inputMode="decimal"
                  invalid={!!errors.taxRates?.[index]?.rate}
                  {...register(`taxRates.${index}.rate`)}
                />
              </Field>
              <label
                htmlFor={`rate-default-${index}`}
                className="flex items-center gap-2 pb-2 text-sm text-app-text"
              >
                <Checkbox.Root
                  inputId={`rate-default-${index}`}
                  checked={Boolean(values.taxRates[index]?.isDefault)}
                  onCheckedChange={(event: { checked: boolean }) =>
                    setValue(`taxRates.${index}.isDefault`, event.checked, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                >
                  <Checkbox.Box>
                    <Checkbox.Indicator match="checked">
                      <i className="pi pi-check" />
                    </Checkbox.Indicator>
                  </Checkbox.Box>
                </Checkbox.Root>
                <span>{t("setup.vat.isDefault")}</span>
              </label>
              {rates.fields.length > 1 ? (
                <Button
                  type="button"
                  variant="text"
                  size="small"
                  onClick={() => rates.remove(index)}
                  className="mb-0.5"
                >
                  {t("common.remove")}
                </Button>
              ) : null}
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outlined"
          severity="secondary"
          size="small"
          className="mt-3"
          onClick={() =>
            rates.append({
              name: "",
              rate: "",
              isDefault: false,
            })
          }
        >
          {t("setup.vat.addRate")}
        </Button>
      </fieldset>
    </div>
  );
}

export function BankStep() {
  const { t, register, setValue, errors, values } = useSetup();
  return (
    <div className="space-y-4">
      <p className="text-sm text-app-muted">{t("setup.bank.description")}</p>
      <Field
        label={t("setup.bank.iban")}
        htmlFor="iban"
        error={translateError(errors.iban?.message, t)}
        hint={
          values.referenceType === "QRR"
            ? t("setup.bank.qrIbanHint")
            : t("setup.bank.ibanHint")
        }
        required={values.referenceType === "QRR"}
      >
        <Input
          id="iban"
          className="font-mono"
          autoComplete="off"
          invalid={!!errors.iban}
          {...register("iban")}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t("setup.bank.bankName")}
          htmlFor="bank-name"
          error={translateError(errors.bankName?.message, t)}
        >
          <Input id="bank-name" {...register("bankName")} />
        </Field>
        <Field
          label={t("setup.bank.referenceType")}
          htmlFor="reference-type"
          required
        >
          <Select.Root
            id="reference-type"
            value={values.referenceType}
            onValueChange={(event: { value: unknown }) =>
              setValue(
                "referenceType",
                event.value as SetupForm["referenceType"],
                {
                  shouldDirty: true,
                  shouldValidate: true,
                },
              )
            }
            fluid
            options={[
              {
                value: "NON",
                label: t("setup.bank.refNON"),
              },
              {
                value: "SCOR",
                label: t("setup.bank.refSCOR"),
              },
              {
                value: "QRR",
                label: t("setup.bank.refQRR"),
              },
            ]}
            optionLabel="label"
            optionValue="value"
          >
            <Select.Trigger
              type="button"
              aria-label={t("setup.bank.referenceType")}
            >
              <Select.Value />
              <Select.Indicator className="pi pi-chevron-down" />
            </Select.Trigger>
            <Select.Portal>
              <Select.Positioner>
                <Select.Popup>
                  <Select.List />
                </Select.Popup>
              </Select.Positioner>
            </Select.Portal>
          </Select.Root>
        </Field>
      </div>
    </div>
  );
}

export function NumberingStep() {
  const { t, setValue, errors, values, year } = useSetup();
  return (
    <div className="space-y-4">
      <p className="text-sm text-app-muted">
        {t("setup.numbering.description")}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t("setup.numbering.paymentTermDays")}
          htmlFor="payment-terms"
          error={translateError(errors.paymentTermDays?.message, t)}
          required
        >
          <InputNumber.Root
            fluid
            id="payment-terms"
            min={0}
            max={365}
            invalid={!!errors.paymentTermDays}
            value={values.paymentTermDays}
            onValueChange={(event: { value: number | null }) =>
              setValue("paymentTermDays", event.value ?? 0, {
                shouldValidate: true,
                shouldTouch: true,
              })
            }
          >
            <InputNumber.Input as={Input} />
          </InputNumber.Root>
        </Field>
        <Field
          label={t("setup.numbering.offerValidityDays")}
          htmlFor="offer-validity"
          error={translateError(errors.offerValidityDays?.message, t)}
          required
        >
          <InputNumber.Root
            fluid
            id="offer-validity"
            min={1}
            max={365}
            invalid={!!errors.offerValidityDays}
            value={values.offerValidityDays}
            onValueChange={(event: { value: number | null }) =>
              setValue("offerValidityDays", event.value ?? 1, {
                shouldValidate: true,
                shouldTouch: true,
              })
            }
          >
            <InputNumber.Input as={Input} />
          </InputNumber.Root>
        </Field>
      </div>
      <Card.Root padded={false} className="overflow-hidden">
        <Card.Body>
          <Card.Content>
            <h3 className="border-b border-app-border px-4 py-2.5 text-sm font-medium text-app-text">
              {t("setup.numbering.previewTitle")}
            </h3>
            <dl className="divide-y divide-app-border">
              {(
                [
                  ["customer", "CUS-000001"],
                  ["request", `REQ-${year}-000001`],
                  ["offer", `OFF-${year}-000001`],
                  ["project", `PRJ-${year}-000001`],
                  ["invoice", `INV-${year}-000001`],
                ] as const
              ).map(([key, example]) => (
                <div
                  key={key}
                  className="flex items-center justify-between px-4 py-2.5"
                >
                  <dt className="text-sm text-app-muted">
                    {t(`setup.numbering.${key}`)}
                  </dt>
                  <dd className="font-mono text-sm text-app-text">{example}</dd>
                </div>
              ))}
            </dl>
          </Card.Content>
        </Card.Body>
      </Card.Root>
    </div>
  );
}

export function ReviewStep() {
  const { t, values, submitError, countryName } = useSetup();
  return (
    <div className="space-y-4">
      <p className="text-sm text-app-muted">{t("setup.review.description")}</p>
      <ReviewSection title={t("setup.review.companySection")}>
        <ReviewRow
          label={t("setup.company.name")}
          value={values.company.name}
        />
        <ReviewRow
          label={t("setup.industry.label", {
            defaultValue: "Industry",
          })}
          value={t(`setup.industry.options.${values.company.industry}`, {
            defaultValue: INDUSTRY_DEFAULT_LABELS[values.company.industry],
          })}
        />
        <ReviewRow
          label={t("setup.address.title")}
          value={[
            values.company.addressLine1,
            `${values.company.postalCode} ${values.company.city}`,
            countryName(values.company.country),
          ]
            .filter(Boolean)
            .join(", ")}
        />
        <ReviewRow label={t("common.email")} value={values.company.email} />
      </ReviewSection>
      <ReviewSection title={t("setup.review.systemAdminSection")}>
        <ReviewRow label={t("setup.systemAdmin.username")} value="admin" />
      </ReviewSection>
      <ReviewSection title={t("setup.review.localizationSection")}>
        <ReviewRow
          label={t("setup.localization.locale")}
          value={t(`locale.${values.localization.defaultLocale}`)}
        />
        <ReviewRow
          label={t("setup.localization.timezone")}
          value={values.localization.timezone}
        />
        <ReviewRow
          label={t("setup.localization.currency")}
          value={values.localization.currency}
        />
      </ReviewSection>
      <ReviewSection title={t("setup.review.billingSection")}>
        <ReviewRow
          label={t("setup.vat.registered")}
          value={values.vatRegistered ? t("common.yes") : t("common.no")}
        />
        <ReviewRow
          label={t("setup.vat.rates")}
          value={values.taxRates
            .map((rate) => `${rate.name} ${rate.rate}%`)
            .join(", ")}
        />
        <ReviewRow label={t("setup.bank.iban")} value={values.iban || "—"} />
        <ReviewRow
          label={t("setup.bank.referenceType")}
          value={values.referenceType}
        />
        <ReviewRow
          label={t("setup.numbering.paymentTermDays")}
          value={String(values.paymentTermDays)}
        />
      </ReviewSection>
      <ReviewSection title={t("setup.review.documentsSection")}>
        <ReviewRow
          label={t("setup.numbering.offerValidityDays")}
          value={String(values.offerValidityDays)}
        />
      </ReviewSection>
      {submitError ? (
        <p
          role="alert"
          className="rounded-lg bg-danger-soft px-4 py-3 text-sm text-danger"
        >
          {submitError}
        </p>
      ) : null}
    </div>
  );
}
